import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Heart, Users, Star, Award, Mail } from 'lucide-react';

const Mentor = () => {
  const benefits = [
    { icon: Heart, title: 'Make a Difference', description: 'Shape the future of Indigenous young women leaders' },
    { icon: Users, title: 'Build Connections', description: 'Join a community of dedicated mentors and leaders' },
    { icon: Star, title: 'Share Your Wisdom', description: 'Pass on cultural knowledge and life skills' },
    { icon: Award, title: 'Personal Growth', description: 'Develop your own leadership and mentoring abilities' }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 md:py-24" style={{ backgroundColor: '#dbbf57' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Become a Mentor
            </h1>
            <p className="text-lg md:text-xl leading-relaxed opacity-95">
              Share your experience, wisdom, and passion with the next generation of Indigenous women leaders. Your guidance can change lives.
            </p>
          </div>
        </div>
      </section>

      {/* Why Mentor */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center mb-12" style={{ color: '#7e2f22' }}>Why Become a Mentor?</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {benefits.map((benefit, index) => {
                const IconComponent = benefit.icon;
                return (
                  <Card key={index} className="border-0 shadow-lg">
                    <CardContent className="pt-8 pb-8 text-center">
                      <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#dbbf5720' }}>
                        <IconComponent className="h-8 w-8" style={{ color: '#dbbf57' }} />
                      </div>
                      <h3 className="text-lg font-bold mb-2" style={{ color: '#7e2f22' }}>{benefit.title}</h3>
                      <p className="text-sm text-gray-600">{benefit.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* What We Look For */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-2xl" style={{ color: '#7e2f22' }}>What We Look For in Mentors</CardTitle>
                <CardDescription>Our ideal mentors have:</CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#53a5ca' }} />
                    <span>Passion for empowering Indigenous young women</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#53a5ca' }} />
                    <span>Understanding of and connection to Indigenous culture and traditions</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#53a5ca' }} />
                    <span>Life experience and skills to share (in any area - arts, education, leadership, etc.)</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#53a5ca' }} />
                    <span>Commitment to attend at least one event per month</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#53a5ca' }} />
                    <span>Ability to pass background check (required for working with youth)</span>
                  </li>
                  <li className="flex items-start space-x-3">
                    <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0" style={{ backgroundColor: '#53a5ca' }} />
                    <span>Patience, kindness, and genuine interest in youth development</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact to Apply */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Card className="border-0 shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl text-center" style={{ color: '#7e2f22' }}>Interested in Becoming a Mentor?</CardTitle>
                <CardDescription className="text-center text-lg">
                  We'd love to hear from you! Reach out to learn more about mentorship opportunities.
                </CardDescription>
              </CardHeader>
              <CardContent className="text-center py-8">
                <p className="text-gray-700 mb-8 text-lg leading-relaxed max-w-2xl mx-auto">
                  If you're passionate about empowering Indigenous young women and want to share your knowledge and experience, we invite you to connect with us. Send us an email to express your interest and we'll be in touch with next steps.
                </p>
                <a href="mailto:naiwadaughters@gmail.com?subject=Mentor Application Inquiry">
                  <Button 
                    size="lg" 
                    className="text-white hover:opacity-90 transition-all duration-200 text-lg px-12 py-6"
                    style={{ backgroundColor: '#dbbf57', color: '#7e2f22' }}
                  >
                    <Mail className="mr-3 h-6 w-6" />
                    Email Us to Apply
                  </Button>
                </a>
                <p className="text-sm text-gray-500 mt-6">
                  naiwadaughters@gmail.com
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Mentor;